import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSEO from '@/hooks/useSEO';
import { getByType, searchContent, type ContentItem, type ContentType, typeLabels } from '@/lib/content';

const tabs: { key: 'all' | ContentType; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'story', label: '剧情' },
  { key: 'cast', label: '演员' },
  { key: 'news', label: '资讯' },
];

const hotKeywords = ['陈默', '江暖', '雨夜', '幕后', '结局', '陆嘉行'];

function Highlight({ text, keyword }: { text: string; keyword: string }) {
  const trimmed = keyword.trim();
  if (!trimmed) return <>{text}</>;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === trimmed.toLowerCase() ? (
          <mark key={index} className="rounded bg-primary-500/25 px-0.5 text-primary-200">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

function ResultRow({ item, keyword }: { item: ContentItem; keyword: string }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group grid grid-cols-1 gap-4 rounded-lg border border-background-200/80 bg-background-100 p-3 transition-colors hover:border-primary-500/40 sm:grid-cols-[160px_1fr] md:p-4"
    >
      <div className="h-36 w-full overflow-hidden rounded-md bg-background-200 sm:h-full sm:min-h-[110px]">
        <img
          src={item.image}
          alt={`${item.title} 配图`}
          title={`${item.title} - 搜索结果`}
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-105"
        />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded-full bg-accent-900/60 px-2.5 py-0.5 text-accent-200">
            {typeLabels[item.type]}
          </span>
          <span className="text-foreground-500">{item.dateLabel}</span>
        </div>
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-400">
          <Highlight text={item.title} keyword={keyword} />
        </h3>
        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-400">
          <Highlight text={item.excerpt} keyword={keyword} />
        </p>
        <span className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-500 transition-colors group-hover:text-primary-400">
          查看详情
          <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<'all' | ContentType>('all');

  useEffect(() => {
    setInput(query);
    setTab('all');
  }, [query]);

  useSEO({
    title: query ? `搜索“${query}”的结果 - 我的朋友他的妻子` : '站内搜索 - 我的朋友他的妻子',
    description: query
      ? `在《我的朋友他的妻子》影视站中搜索“${query}”，覆盖剧情介绍、演员资料与影视资讯三类内容，支持按分类筛选结果。`
      : '搜索《我的朋友他的妻子》的剧情节点、演员资料与影视资讯，支持跨内容库关键词检索与分类筛选。',
    keywords: '站内搜索,我的朋友他的妻子,剧情,演员,资讯',
  });

  const results = useMemo(() => searchContent(query), [query]);
  const grouped = useMemo(
    () => ({
      story: results.filter((item) => item.type === 'story'),
      cast: results.filter((item) => item.type === 'cast'),
      news: results.filter((item) => item.type === 'news'),
    }),
    [results],
  );

  const tabCounts: Record<'all' | ContentType, number> = {
    all: results.length,
    story: grouped.story.length,
    cast: grouped.cast.length,
    news: grouped.news.length,
  };

  const visible = tab === 'all' ? results : results.filter((item) => item.type === tab);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    setParams(next ? { q: next } : {});
  };

  const totalPool =
    getByType('story').length + getByType('cast').length + getByType('news').length;

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200/70 bg-background-100/50 px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
        <div className="mt-6 max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-primary-500"></span>
            <span className="font-label text-xs tracking-[0.3em] text-primary-400">SEARCH</span>
          </div>
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {query ? (
              <>
                搜索“<span className="text-primary-400">{query}</span>”
              </>
            ) : (
              '站内搜索'
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-400">
            站内共收录 {totalPool} 条内容，覆盖剧情、演员与资讯三类，检索结果即时展示并高亮关键词。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 focus-within:border-primary-500/60">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                name="q"
                placeholder="输入关键词，例如：陈默、雨夜、幕后"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-600 focus:outline-none"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput('')}
                  aria-label="清空关键词"
                  className="text-foreground-500 transition-colors hover:text-primary-400 cursor-pointer"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400 cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-600">热门搜索：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setParams({ q: word })}
                className="rounded-full border border-background-300/70 px-3 py-1 text-xs text-foreground-400 transition-colors hover:border-primary-500/50 hover:text-primary-300 cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        {query ? (
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-400">
                共找到 <span className="text-primary-400">{results.length}</span> 条与“
                <span className="text-foreground-900">{query}</span>”相关的内容
              </p>
              <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300/70 bg-background-100 p-1">
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors cursor-pointer ${
                      tab === item.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-400 hover:text-foreground-950'
                    }`}
                  >
                    {item.label} {tabCounts[item.key]}
                  </button>
                ))}
              </div>
            </div>

            {results.length === 0 ? (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 px-4 py-16 text-center">
                <i className="ri-ghost-line text-3xl text-foreground-600"></i>
                <p className="mt-3 text-sm text-foreground-300">未找到相关内容</p>
                <p className="mt-1 max-w-md text-xs leading-relaxed text-foreground-500">
                  试试更短的关键词，或者换一个方向：剧情可以用“雨夜”“海边”，演员可以用姓名，资讯可以用“幕后”“结局”。
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setInput('');
                      setParams({});
                    }}
                    className="rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-400 cursor-pointer"
                  >
                    重置搜索
                  </button>
                  <Link
                    to="/"
                    className="rounded-md border border-background-300/70 px-4 py-2 text-xs text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-8">
                {tab === 'all' ? (
                  (['story', 'cast', 'news'] as ContentType[]).map((type) =>
                    grouped[type].length > 0 ? (
                      <div key={type}>
                        <div className="flex items-center justify-between gap-3">
                          <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                            <span className="h-px w-6 bg-primary-500"></span>
                            {typeLabels[type]}
                            <span className="text-xs font-normal text-foreground-500">
                              {grouped[type].length} 条
                            </span>
                          </h2>
                          <button
                            type="button"
                            onClick={() => setTab(type)}
                            className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-400 cursor-pointer"
                          >
                            只看该类 &gt;
                          </button>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                          {grouped[type].map((item) => (
                            <ResultRow key={item.id} item={item} keyword={query} />
                          ))}
                        </div>
                      </div>
                    ) : null,
                  )
                ) : (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {visible.map((item) => (
                      <ResultRow key={item.id} item={item} keyword={query} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {[
              { icon: 'ri-book-open-line', title: '按剧情找', desc: '用“雨夜”“海边”“空座”这类场景词，能更快定位到具体哪一幕。', to: '/story' },
              { icon: 'ri-user-star-line', title: '按演员找', desc: '输入演员姓名，或直接搜角色名，例如“陈默”“江暖”。', to: '/cast' },
              { icon: 'ri-newspaper-line', title: '按资讯找', desc: '“幕后”“剧评”“专访”等栏目词，可以找到对应的报道。', to: '/news' },
            ].map((card) => (
              <Link
                key={card.title}
                to={card.to}
                className="group rounded-lg border border-background-200/80 bg-background-100 p-5 transition-colors hover:border-primary-500/40"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200/70 text-primary-400">
                  <i className={`${card.icon} text-lg`}></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-400">
                  {card.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{card.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs text-foreground-500">
                  去分类页
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </SiteLayout>
  );
}